import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const SECTION_TYPES = [
  { value: "wallet", label: "Wallet" },
  { value: "giftcard", label: "Gift Card" },
  { value: "referral", label: "Referral" },
  { value: "app_section", label: "App Section" },
  { value: "credit_line", label: "Credit Line" },
  { value: "terms_and_condition", label: "Terms and Condition" },
  { value: "privacy_policy", label: "Privacy Policy" },
];

export default function PolicyPagesList() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sectionType, setSectionType] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchPages = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;

    setLoading(true);
    setErr("");

    try {
      const res = await api.get("/admin/policy-pages", {
        params: {
          search: search || undefined,
          status: status !== "" ? status : undefined,
          section_type: sectionType || undefined,
          page: nextPage,
          limit: nextLimit,
          sortBy: "created_at",
          order: "DESC",
        },
      });

      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load policy pages";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages({ page: 1 });
  }, []);

  const onApply = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPages({ page: 1 });
  };

  const openCreate = () => {
    navigate("/admin/policy-pages/create");
  };

  const openEdit = (row) => {
    navigate(`/admin/policy-pages/edit/${row.policy_page_id}`);
  };

  const deactivate = async (policy_page_id) => {
    const ok = window.confirm("Deactivate this policy page?");
    if (!ok) return;

    try {
      await api.delete(`/admin/policy-pages/${policy_page_id}`);
      toast.success("Policy page deactivated");
      await fetchPages();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Deactivate failed");
    }
  };

  const deletePage = async (policy_page_id) => {
    const ok = window.confirm("Permanently delete this policy page? This cannot be undone.");
    if (!ok) return;

    try {
      await api.delete(`/admin/policy-pages/${policy_page_id}/hard`);
      toast.success("Policy page deleted");
      await fetchPages();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Delete failed");
    }
  };

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchPages({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchPages({ page: p });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 10;
    setLimit(v);
    setPage(1);
    fetchPages({ page: 1, limit: v });
  };

  const sectionLabel = (value) =>
    SECTION_TYPES.find((s) => s.value === value)?.label || value;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Policy Pages</h1>
          <p className="text-sm text-gray-600 mt-1">Manage policy content shown under Wallet, Gift Card, Referral and App sections.</p>
        </div>

        <button
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 self-start"
          onClick={openCreate}
        >
          + Create Policy Page
        </button>
      </div>

      {/* Filters */}
      <form onSubmit={onApply} className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Search by title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="min-w-[200px]">
          <label className="text-sm font-semibold">Section Type</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={sectionType}
            onChange={(e) => setSectionType(e.target.value)}
          >
            <option value="">All</option>
            {SECTION_TYPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[200px]">
          <label className="text-sm font-semibold">Status</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <div className="min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <button
          type="submit"
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
        >
          Apply
        </button>
      </form>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      {/* Table */}
      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Policy Pages List</span>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>

            <button
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No policy pages found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Section Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="border-t">
              {rows.map((p) => (
                <tr key={p.policy_page_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{p.title}</td>
                  <td className="px-4 py-3">{sectionLabel(p.section_type)}</td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                        Number(p.status) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {Number(p.status) === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openEdit(p)}
                      >
                        Edit
                      </button>

                      {Number(p.status) === 1 ? (
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => deactivate(p.policy_page_id)}
                        >
                          Deactivate
                        </button>
                      ) : null}

                      <button
                        type="button"
                        className="rounded-lg border border-red-200 text-red-600 px-3 py-1 text-xs font-semibold hover:bg-red-50"
                        onClick={() => deletePage(p.policy_page_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
